import React, { useRef, useState, useEffect } from 'react';
import { Step, Steps } from 'react-step-builder';
import countries from '~/data/countries';
import JoinConfirmation from '~/components/JoinConfirmation';
import { xor } from 'lodash';
import { useKeyPress } from 'react-use';
import * as Yup from 'yup';

const schema = Yup.object().shape({
  firstName: Yup.string().required(),
  lastName: Yup.string().required(),
  country: Yup.string().required(),
  city: Yup.string().required(),
  email: Yup.string().email(),
  phone_number: Yup.string().required(),
  camera: Yup.string().required(),
  lens: Yup.string().required(),
  genre: Yup.array().min(1),
  portfolio: Yup.string().required(),
  source: Yup.string().required(),
  acceptTerms: Yup.boolean().oneOf([true]),
});

const debug = false;

const submitForm = async (answers) => {
  const response = await fetch('/.netlify/functions/fountain-form', {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    method: 'POST',
    body: JSON.stringify(answers),
  });

  const json = await response.json();
  return json;
};

export default ({ fountainFormFields, confirmation, isSubmitted, setIsSubmitted }) => {
  const [answers, setAnswers] = useState({
    firstName: '',
    lastName: '',
    country: '',
    city: '',
    email: '',
    phone_number: '',
    camera: '',
    lens: '',
    genre: '',
    portfolio: '',
    source: '',
    acceptTerms: false,
  });

  const setAnswerItem = (key, val) => {
    return setAnswers({
      ...answers,
      [key]: val,
    });
  };

  return (
    <div
      className={`-mt-24 -mb-24 min-h-screen flex flex-col justify-center items-center ${isSubmitted && 'bg-black'}`}
    >
      {!isSubmitted ? (
        <div className="container  py-32">
          <div className="max-w-xl w-full mx-auto flex items-end">
            <Steps
              config={{
                navigation: {
                  component: (props) => {
                    return <Navigation {...props} answers={answers} setIsSubmitted={setIsSubmitted} />;
                  },
                  location: 'after',
                },
              }}
            >
              {fountainFormFields.map((q, i) => {
                return (
                  <Step
                    key={`q${i}`}
                    title={q.name}
                    questions={fountainFormFields}
                    answers={answers}
                    setAnswerItem={setAnswerItem}
                    component={StepItem}
                    name={q.name}
                    heading={q.heading}
                    type={q.type}
                    placeholder={q.placeholder}
                    options={q.selectOptions}
                  />
                );
              })}
            </Steps>
          </div>
        </div>
      ) : (
        <JoinConfirmation confirmation={confirmation} />
      )}
    </div>
  );
};

function StepItem({
  current,
  next,
  heading,
  type,
  placeholder = '',
  name = '',
  options,
  setAnswerItem,
  answers,
  allSteps,
}) {
  const inputRef = useRef();
  useEffect(() => {
    inputRef?.current && inputRef?.current?.focus();
  }, [inputRef]);

  return (
    <div className={`max-w-xl w-full mx-auto mr-6 ${type !== 'multiselect' && type !== 'checkbox' && 'border-b'}`}>
      <div className="mb-3 text-16px tracking-wide">
        <span className="font-bold">{current < 10 ? '0' + current : current}</span>{' '}
        <span className="opacity-75"> | {allSteps.length}</span>
      </div>

      <h3 className="text-h3 mb-6">{heading}</h3>

      {(type === 'text' || type === 'email') && (
        <input
          className="h-12 !outline-none block w-full transition-all duration-200 ease-in-out"
          ref={inputRef}
          name={name}
          type={type}
          value={answers[name]}
          placeholder={placeholder}
          onChange={({ target: { value } }) => setAnswerItem(name, value)}
          required
        />
      )}

      {type === 'select' && (
        <select
          className="h-12 pl-4 block w-full"
          name={name}
          ref={inputRef}
          onChange={({ target: { value } }) => setAnswerItem(name, value)}
        >
          <option value="#" selected disabled>
            {placeholder}
          </option>

          {options[0].value === 'country' ? (
            <>
              {countries.map((country, i) => (
                <option key={country + i} value={country} dangerouslySetInnerHTML={{ __html: country }} />
              ))}
            </>
          ) : (
            <>
              {options.map((option) => (
                <option key={option.value} value={option.value} dangerouslySetInnerHTML={{ __html: option.heading }} />
              ))}
            </>
          )}
        </select>
      )}

      {type === 'multiselect' && (
        <div className="mt-8">
          <div className="flex flex-wrap -ml-4 -mb-4">
            {options.map((option) => (
              <div key={option.value} className="pl-4 mb-4">
                <div
                  className={`py-2 px-4 border cursor-pointer select-none ${
                    answers[name].includes(option.value) && 'bg-blue text-white border-blue'
                  } transition duration-100 ease-in-out`}
                  onClick={() => setAnswerItem(name, xor([option.value], answers[name]))}
                  dangerouslySetInnerHTML={{ __html: option.heading }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {type === 'checkbox' && (
        <div className="select-none mt-[.5rem] mr-6">
          <div onClick={() => setAnswerItem(name, !!!answers[name])} className="inline-flex cursor-pointer group">
            <div className="flex items-start -ml-4">
              <div className="pl-4 mt-0.5">
                <button
                  className={`h-3 w-3 border opacity-100 transition-opacity duration-200 group-hover:opacity-50 ${
                    answers[name] === true && 'bg-black'
                  }`}
                  role="checkbox"
                />
              </div>
              <div className={`pl-4`} dangerouslySetInnerHTML={{ __html: placeholder }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Navigation(props) {
  const { current, progress, prev, next, allSteps, answers, setIsSubmitted } = props;
  const [canMoveToNextStep, setCanMoveToNextStep] = useState(false);

  useEffect(()=>{
    setCanMoveToNextStep(false);
  },[current]);

  useEffect(() => {
    const currentStepName = allSteps[current - 1]?.title;
    // console.log(answers[currentStepName].length > 0 || answers[currentStepName] === true);

      try {
        Yup.reach(schema, currentStepName).validate(answers[currentStepName]).then((v)=>{
          // console.log(v);
          setCanMoveToNextStep(answers[currentStepName].length > 0 || answers[currentStepName] === true);
        }).catch(e => console.log(e));

      } catch (error) {
        console.log(error);
      }

  });

  const goToNext = async () => {
    const isLast = progress === 1;

    if (isLast && canMoveToNextStep) {
      const response = await submitForm(answers);
      console.log(response);
      setIsSubmitted(true);
      return;
    }

    if (canMoveToNextStep) {
      return next();
    }
  };

  const [isPressed] = useKeyPress('Enter');
  useEffect(() => {
    if (isPressed === true) {
      goToNext();
    }
  }, [isPressed]);

  return (
    <div className="flex space-x-5 mt-6 select-none">
      <svg
        onClick={goToNext}
        className={`${!canMoveToNextStep ? 'opacity-75' : 'cursor-pointer'} transition-opacity duration-150`}
        width="50"
        height="50"
        viewBox="0 0 73 73"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="36.473"
          cy="36.641"
          r="35.574"
          fill={canMoveToNextStep ? '#4B3CF9' : 'transparent'}
          stroke={canMoveToNextStep ? '#4B3CF9' : '#101010'}
          strokeWidth="1.5"
        />
        <mask id="circlemask" fill="#fff">
          <path d="M33.813 42.15l7.245-5.968-7.245-5.97" />
        </mask>
        <path
          d="M41.058 36.181l.953 1.158 1.406-1.157-1.406-1.158-.953 1.157zm-6.292 7.128l7.245-5.97-1.907-2.315-7.245 5.97 1.907 2.315zm7.245-8.285l-7.245-5.97-1.907 2.316 7.245 5.97 1.907-2.316z"
          fill={canMoveToNextStep ? '#fff' : '#101010'}
          mask="url(#circlemask)"
        />
      </svg>
    </div>
  );
}
